import React from "react";
import BreadCrumb from "@/src/components/Shared/BreadCrumbs";
import Head from "next/head";

import server from "@/src/api/server";
import { useIntl } from "react-intl";
import styles from "@/styles/partners.module.scss";
import { checkRedirect } from "@/src/components/helpers/functions";
import Countries from "@/src/components/Home/Countries";
import Seo from "@/src/components/Shared/Next-seo";

const recruitmentCountriesSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      "@id": "https://alnjoom.sa/recruitmentCountries/#breadcrumb",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "الرئيسية",
          item: "https://alnjoom.sa/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "دول الاستقدام",
          item: "https://alnjoom.sa/recruitmentCountries/",
        },
      ],
    },
    {
      "@type": "CollectionPage",
      "@id": "https://alnjoom.sa/recruitmentCountries/#webpage",
      url: "https://alnjoom.sa/recruitmentCountries/",
      name: "دول الاستقدام - مكتب النجوم المميزة للاستقدام",
      description:
        "تصفح جميع دول الاستقدام المعتمدة لدى مكتب النجوم المميزة للاستقدام: بنغلاديش، أثيوبيا، الفلبين، باكستان، كينيا، وبوروندي، واختر العاملة المنزلية المناسبة لك.",
      inLanguage: "ar",
      isPartOf: { "@id": "https://alnjoom.sa/#website" },
      breadcrumb: { "@id": "https://alnjoom.sa/recruitmentCountries/#breadcrumb" },
      mainEntity: { "@id": "https://alnjoom.sa/recruitmentCountries/#itemlist" },
    },
    {
      "@type": "ItemList",
      "@id": "https://alnjoom.sa/recruitmentCountries/#itemlist",
      name: "دول الاستقدام المتاحة",
      numberOfItems: 6,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          url: "https://alnjoom.sa/recruitmentCountries/FVOfWFcbM/",
          item: {
            "@type": "Service",
            name: "استقدام عاملة منزلية من بنغلاديش",
            url: "https://alnjoom.sa/recruitmentCountries/FVOfWFcbM/",
            serviceType: "استقدام عمالة منزلية",
            areaServed: { "@type": "Country", name: "المملكة العربية السعودية" },
            provider: { "@id": "https://alnjoom.sa/#organization" },
          },
        },
        {
          "@type": "ListItem",
          position: 2,
          url: "https://alnjoom.sa/recruitmentCountries/7qxEJng5g/",
          item: {
            "@type": "Service",
            name: "استقدام عاملة منزلية من أثيوبيا",
            url: "https://alnjoom.sa/recruitmentCountries/7qxEJng5g/",
            serviceType: "استقدام عمالة منزلية",
            areaServed: { "@type": "Country", name: "المملكة العربية السعودية" },
            provider: { "@id": "https://alnjoom.sa/#organization" },
          },
        },
        {
          "@type": "ListItem",
          position: 3,
          url: "https://alnjoom.sa/recruitmentCountries/i7beLSowJ/",
          item: {
            "@type": "Service",
            name: "استقدام عاملة منزلية من الفلبين",
            url: "https://alnjoom.sa/recruitmentCountries/i7beLSowJ/",
            serviceType: "استقدام عمالة منزلية",
            areaServed: { "@type": "Country", name: "المملكة العربية السعودية" },
            provider: { "@id": "https://alnjoom.sa/#organization" },
          },
        },
        {
          "@type": "ListItem",
          position: 4,
          url: "https://alnjoom.sa/recruitmentCountries/cAqU9YF2n/",
          item: {
            "@type": "Service",
            name: "استقدام عاملة منزلية من باكستان",
            url: "https://alnjoom.sa/recruitmentCountries/cAqU9YF2n/",
            serviceType: "استقدام عمالة منزلية",
            areaServed: { "@type": "Country", name: "المملكة العربية السعودية" },
            provider: { "@id": "https://alnjoom.sa/#organization" },
          },
        },
        {
          "@type": "ListItem",
          position: 5,
          url: "https://alnjoom.sa/recruitmentCountries/4CfY7t_MR/",
          item: {
            "@type": "Service",
            name: "استقدام عاملة منزلية من كينيا",
            url: "https://alnjoom.sa/recruitmentCountries/4CfY7t_MR/",
            serviceType: "استقدام عمالة منزلية",
            areaServed: { "@type": "Country", name: "المملكة العربية السعودية" },
            provider: { "@id": "https://alnjoom.sa/#organization" },
          },
        },
        {
          "@type": "ListItem",
          position: 6,
          url: "https://alnjoom.sa/recruitmentCountries/oqGSCVMrm/",
          item: {
            "@type": "Service",
            name: "استقدام عاملة منزلية من بوروندي",
            url: "https://alnjoom.sa/recruitmentCountries/oqGSCVMrm/",
            serviceType: "استقدام عمالة منزلية",
            areaServed: { "@type": "Country", name: "المملكة العربية السعودية" },
            provider: { "@id": "https://alnjoom.sa/#organization" },
          },
        },
      ],
    },
  ],
};

const Page = ({ countriesData, personnelsData, seoData }) => {
  const { locale, formatMessage } = useIntl();

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(recruitmentCountriesSchema),
          }}
        />
      </Head>
      {seoData?.title?.[locale] && (
        <Seo
          title={seoData?.title?.[locale]}
          description={seoData?.description?.[locale]}
          keywords={seoData?.keywords?.[locale]}
        />
      )}
      <BreadCrumb
        heading={formatMessage({ id: "recruitmentCountries" })}
        description={`${formatMessage({
          id: "main",
        })} / ${formatMessage({
          id: "recruitmentCountries",
        })}`}
        background={countriesData?.breadCurmbImage}
      />
      <div className={styles.aboutPage}>
        <Countries
          data={countriesData}
          personnel={personnelsData}
          page="partners"
        />
      </div>
    </>
  );
};

export async function getServerSideProps(ctx) {
  const redirect = await checkRedirect(ctx.resolvedUrl, ctx.req);
  if (redirect) return { redirect };

  const [homeResponse, countriesResponse, personnelsResponse, seoPageResponse] =
    await Promise.all([
      server.get("/pages/home"),
      server.get("/countries"),
      server.get("/personnels"),
      server.get("/pages/seo-data"),
    ]);

  const sectionData = (r) => r?.data?.data?.page?.sections || [];

  const getData = (section, response) =>
    sectionData(response)?.filter((e) => e?.slug == section)?.[0] || [];

  const homeData = (section) =>
    sectionData(homeResponse)?.filter((e) => e?.slug == section)?.[0] || [];
  const seoData = sectionData(seoPageResponse)?.find(
    (section) => section?.pageName === "recruitment-countries",
  );

  return {
    props: {
      countriesData: {
        heading: getData("countries", homeResponse)?.heading || "",
        description: getData("countries", homeResponse)?.description || "",
        breadCurmbImage: homeData("intro")?.items[0]?.backgroundImage,
        items: countriesResponse?.data?.data?.countries,
      },
      personnelsData: {
        items: personnelsResponse?.data?.data?.personnels,
      },
      seoData,
    },
  };
}

export default Page;
